import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Calculator,
  Settings,
  Wallet,
  RefreshCw,
  Receipt,
  Landmark,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAccountStore } from "@/features/accounts/store/useAccountStore";
import { useFeatureFlagStore } from "@/features/feature-flags/store/useFeatureFlagStore";
import { FeatureFlag } from "@/features/feature-flags/types/featureFlags.types";

const NAV_ITEMS = [
  { to: "/accounts", label: "Accounts", Icon: Landmark },
  { to: "/", label: "Summary", Icon: LayoutDashboard },
  { to: "/budget", label: "Budget", Icon: Wallet, flag: FeatureFlag.Budget },
  { to: "/bills", label: "Bills", Icon: Receipt },
  { to: "/friends", label: "Friends", Icon: Users },
  {
    to: "/recurring-expenses",
    label: "Recurring",
    Icon: RefreshCw,
    flag: FeatureFlag.Recurring,
  },
  {
    to: "/tax-planner",
    label: "Tax Plan",
    Icon: Calculator,
    flag: FeatureFlag.TaxPlanner,
  },
  { to: "/settings", label: "Settings", Icon: Settings },
];

export default function BottomNav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const suggestionCount = useAccountStore((s) => s.suggestions.length);
  const flags = useFeatureFlagStore((s) => s.flags);

  // Bill editor has its own back link and a fixed action footer the FAB would cover.
  if (/^\/bills\/[^/]+$/.test(pathname)) return null;

  return (
    <div className="sm:hidden">
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Speed dial container */}
      <div className="fixed bottom-[calc(1.25rem+env(safe-area-inset-bottom))] right-4 z-50 flex flex-col items-end gap-2.5 pointer-events-none">
        {/* Nav items — rendered above FAB */}
        <div className="flex flex-col items-end gap-2 pointer-events-none">
          {NAV_ITEMS.filter(({ flag }) => !flag || flags[flag]).map(
            ({ to, label, Icon }, i) => {
              const active = pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-full bg-card border shadow-md text-sm font-medium transition-all duration-200 ${
                    active
                      ? "border-primary text-primary"
                      : "border-border text-foreground hover:border-primary/50"
                  } ${
                    open
                      ? "translate-y-0 opacity-100 pointer-events-auto"
                      : "translate-y-3 opacity-0 pointer-events-none"
                  }`}
                  style={{ transitionDelay: open ? `${i * 40}ms` : "0ms" }}
                >
                  <Icon
                    className="w-4 h-4 flex-shrink-0"
                    strokeWidth={active ? 2.25 : 1.75}
                  />
                  <span>{label}</span>
                  {to === "/accounts" && suggestionCount > 0 && (
                    <span className="inline-flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium leading-4 text-white">
                      {suggestionCount}
                    </span>
                  )}
                </Link>
              );
            },
          )}
        </div>

        {/* FAB trigger */}
        <Button
          variant="ghost"
          onClick={() => setOpen((o) => !o)}
          className="pointer-events-auto size-12 p-0 rounded-full shadow-lg transition-transform active:scale-95"
          aria-label="Navigation menu"
        >
          <img
            src="/icon_animate.svg"
            className={`size-full transition-transform duration-300 ${open ? "rotate-12 scale-110" : ""}`}
            alt=""
            aria-hidden="true"
          />
        </Button>
      </div>
    </div>
  );
}
